import { supabase } from './supabase'
import { getAccessToken } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

// API Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname.endsWith('.pages.dev')
    ? 'https://ai-accelo-backend.conquest-solutions8334.workers.dev'
    : 'http://localhost:3001/api/v1')

// Helper to make authenticated requests to our backend API
const makeAuthenticatedRequest = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getAccessToken()
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  
  // Add any additional headers from options
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value
      })
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value
      })
    } else {
      Object.assign(headers, options.headers)
    }
  }
  
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  })
}

// Error handling helper
const handleApiError = (error: any, customMessage?: string) => {
  const message = customMessage || error.message || 'An unexpected error occurred'
  toast.error(message)
  throw error
}

// Supabase direct database operations
export const database = {
  // Organizations
  async getOrganizations() {
    try {
      const { data, error } = await supabase
        .from('organization_users')
        .select(`
          role,
          permissions,
          joined_at,
          organization:organizations (
            id,
            name,
            slug,
            description,
            website,
            logo_url
          )
        `)
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch organizations')
    }
  },

  async getOrganization(orgId: string) {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch organization')
    }
  },

  // Projects
  async getProjects(orgId: string, filters?: any) {
    try {
      let query = supabase
        .from('projects')
        .select(`
          *,
          client:clients (
            id,
            name,
            email
          ),
          created_by:users!projects_created_by_fkey (
            id,
            first_name,
            last_name,
            email
          ),
          members:project_members (
            id,
            role,
            user:users (
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }
      if (filters?.client_id) {
        query = query.eq('client_id', filters.client_id)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch projects')
    }
  },

  async getProject(projectId: string) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients (
            id,
            name,
            email,
            phone,
            website
          ),
          created_by:users!projects_created_by_fkey (
            id,
            first_name,
            last_name,
            email
          ),
          members:project_members (
            id,
            role,
            joined_at,
            user:users (
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          )
        `)
        .eq('id', projectId)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch project')
    }
  },

  async createProject(orgId: string, projectData: any) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('projects')
        .insert([{
          ...projectData,
          organization_id: orgId,
          created_by: user.id
        }])
        .select()
        .single()
      
      if (error) throw error
      toast.success('Project created successfully')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to create project')
    }
  },

  async updateProject(projectId: string, projectData: any) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update(projectData)
        .eq('id', projectId)
        .select()
        .single()
      
      if (error) throw error
      toast.success('Project updated successfully')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to update project')
    }
  },

  async deleteProject(projectId: string) {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
      
      if (error) throw error
      toast.success('Project deleted successfully')
    } catch (error) {
      handleApiError(error, 'Failed to delete project')
    }
  },

  // Tasks
  async getTasks(orgId: string, filters?: any) {
    try {
      let query = supabase
        .from('tasks')
        .select(`
          *,
          project:projects (
            id,
            name,
            color
          ),
          assigned_to:users!tasks_assigned_to_fkey (
            id,
            first_name,
            last_name,
            email,
            avatar_url
          ),
          created_by:users!tasks_created_by_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }
      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id)
      }
      if (filters?.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch tasks')
    }
  },

  async createTask(orgId: string, taskData: any) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('tasks')
        .insert([{
          ...taskData,
          organization_id: orgId,
          created_by: user.id
        }])
        .select()
        .single()
      
      if (error) throw error
      toast.success('Task created successfully')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to create task')
    }
  },

  // Time Entries
  async getTimeEntries(orgId: string, filters?: any) {
    try {
      let query = supabase
        .from('time_entries')
        .select(`
          *,
          project:projects (
            id,
            name,
            color
          ),
          task:tasks (
            id,
            title
          ),
          user:users (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('organization_id', orgId)
        .order('start_time', { ascending: false })

      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id)
      }
      if (filters?.user_id) {
        query = query.eq('user_id', filters.user_id)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch time entries')
    }
  },

  async startTimer(orgId: string, timerData: any) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      // Stop any running timers first
      await supabase
        .from('time_entries')
        .update({ 
          status: 'STOPPED',
          end_time: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('status', 'RUNNING')

      const { data, error } = await supabase
        .from('time_entries')
        .insert([{
          ...timerData,
          organization_id: orgId,
          user_id: user.id,
          start_time: new Date().toISOString(),
          status: 'RUNNING'
        }])
        .select()
        .single()
      
      if (error) throw error
      toast.success('Timer started')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to start timer')
    }
  },

  async stopTimer(timeEntryId: string) {
    try {
      const now = new Date().toISOString()
      
      const { data, error } = await supabase
        .from('time_entries')
        .update({ 
          status: 'STOPPED',
          end_time: now
        })
        .eq('id', timeEntryId)
        .select()
        .single()
      
      if (error) throw error
      toast.success('Timer stopped')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to stop timer')
    }
  },

  // Clients
  async getClients(orgId: string, filters?: any) {
    try {
      let query = supabase
        .from('clients')
        .select(`
          *,
          created_by:users (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch clients')
    }
  },

  async createClient(orgId: string, clientData: any) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('clients')
        .insert([{
          ...clientData,
          organization_id: orgId,
          created_by: user.id
        }])
        .select()
        .single()
      
      if (error) throw error
      toast.success('Client created successfully')
      return data
    } catch (error) {
      handleApiError(error, 'Failed to create client')
    }
  },

  // Users
  async getUsers(orgId: string) {
    try {
      const { data, error } = await supabase
        .from('organization_users')
        .select(`
          id,
          role,
          permissions,
          joined_at,
          user:users (
            id,
            email,
            first_name,
            last_name,
            avatar_url,
            status,
            last_login_at
          )
        `)
        .eq('organization_id', orgId)
        .order('joined_at', { ascending: false })
      
      if (error) throw error
      return data
    } catch (error) {
      handleApiError(error, 'Failed to fetch users')
    }
  }
}

// Keep some backend API calls for complex operations
export const backendAPI = {
  // Clients
  async getClients(params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await makeAuthenticatedRequest(`/clients?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch clients')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch clients')
    }
  },

  // Time Tracking
  async getTimeEntries(params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await makeAuthenticatedRequest(`/time?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch time entries')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch time entries')
    }
  },

  async startTimer(timerData: any) {
    try {
      const response = await makeAuthenticatedRequest('/time/start', {
        method: 'POST',
        body: JSON.stringify(timerData)
      })
      if (!response.ok) throw new Error('Failed to start timer')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to start timer')
    }
  },

  async stopTimer(entryId: string) {
    try {
      const response = await makeAuthenticatedRequest(`/time/${entryId}/stop`, {
        method: 'POST'
      })
      if (!response.ok) throw new Error('Failed to stop timer')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to stop timer')
    }
  },

  // Tasks
  async getTasks(params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await makeAuthenticatedRequest(`/tasks?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch tasks')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch tasks')
    }
  },

  async getTask(id: string) {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${id}`)
      if (!response.ok) throw new Error('Failed to fetch task')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch task')
    }
  },

  async createTask(taskData: any) {
    try {
      const response = await makeAuthenticatedRequest('/tasks', {
        method: 'POST',
        body: JSON.stringify(taskData)
      })
      if (!response.ok) throw new Error('Failed to create task')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to create task')
    }
  },

  async updateTask(id: string, taskData: any) {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(taskData)
      })
      if (!response.ok) throw new Error('Failed to update task')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to update task')
    }
  },

  async deleteTask(id: string) {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${id}`, {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Failed to delete task')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to delete task')
    }
  },



  async getDashboardStats(orgId: string) {
    try {
      const response = await makeAuthenticatedRequest(`/dashboard/stats?org=${orgId}`)
      if (!response.ok) throw new Error('Failed to fetch dashboard stats')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch dashboard stats')
    }
  },

  async generateReport(orgId: string, reportType: string, filters: any) {
    try {
      const response = await makeAuthenticatedRequest(`/reports/${reportType}?org=${orgId}`, {
        method: 'POST',
        body: JSON.stringify(filters)
      })
      if (!response.ok) throw new Error('Failed to generate report')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to generate report')
    }
  },

  // ============================================================================
  // TICKETING SYSTEM API
  // ============================================================================
  
  // Tickets
  async getTickets(params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await makeAuthenticatedRequest(`/tickets?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch tickets')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch tickets')
    }
  },

  async createTicket(ticketData: any) {
    try {
      const response = await makeAuthenticatedRequest('/tickets', {
        method: 'POST',
        body: JSON.stringify(ticketData)
      })
      if (!response.ok) throw new Error('Failed to create ticket')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to create ticket')
    }
  },

  async getTicket(ticketId: string) {
    try {
      const response = await makeAuthenticatedRequest(`/tickets/${ticketId}`)
      if (!response.ok) throw new Error('Failed to fetch ticket')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch ticket')
    }
  },

  async updateTicket(ticketId: string, updates: any) {
    try {
      const response = await makeAuthenticatedRequest(`/tickets/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      })
      if (!response.ok) throw new Error('Failed to update ticket')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to update ticket')
    }
  },

  async addTicketComment(ticketId: string, commentData: any) {
    try {
      const response = await makeAuthenticatedRequest(`/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: JSON.stringify(commentData)
      })
      if (!response.ok) throw new Error('Failed to add comment')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to add comment')
    }
  },

  async getTicketComments(ticketId: string, includeInternal = false) {
    try {
      const response = await makeAuthenticatedRequest(
        `/tickets/${ticketId}/comments?includeInternal=${includeInternal}`
      )
      if (!response.ok) throw new Error('Failed to fetch comments')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch comments')
    }
  },

  async escalateTicket(ticketId: string, escalationData: any) {
    try {
      const response = await makeAuthenticatedRequest(`/tickets/${ticketId}/escalate`, {
        method: 'POST',
        body: JSON.stringify(escalationData)
      })
      if (!response.ok) throw new Error('Failed to escalate ticket')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to escalate ticket')
    }
  },

  async getTicketStats(timeframe = '30d') {
    try {
      const response = await makeAuthenticatedRequest(`/tickets/stats?timeframe=${timeframe}`)
      if (!response.ok) throw new Error('Failed to fetch ticket stats')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch ticket stats')
    }
  },

  // ============================================================================
  // KNOWLEDGE BASE API
  // ============================================================================

  // KB Categories
  async getKBCategories() {
    try {
      const response = await makeAuthenticatedRequest('/kb/categories')
      if (!response.ok) throw new Error('Failed to fetch KB categories')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch KB categories')
    }
  },

  async createKBCategory(categoryData: any) {
    try {
      const response = await makeAuthenticatedRequest('/kb/categories', {
        method: 'POST',
        body: JSON.stringify(categoryData)
      })
      if (!response.ok) throw new Error('Failed to create KB category')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to create KB category')
    }
  },

  // KB Articles
  async getKBArticles(params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await makeAuthenticatedRequest(`/kb/articles?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch KB articles')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch KB articles')
    }
  },

  async createKBArticle(articleData: any) {
    try {
      const response = await makeAuthenticatedRequest('/kb/articles', {
        method: 'POST',
        body: JSON.stringify(articleData)
      })
      if (!response.ok) throw new Error('Failed to create KB article')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to create KB article')
    }
  },

  async getKBArticle(articleId: string) {
    try {
      const response = await makeAuthenticatedRequest(`/kb/articles/${articleId}`)
      if (!response.ok) throw new Error('Failed to fetch KB article')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch KB article')
    }
  },

  async updateKBArticle(articleId: string, updates: any) {
    try {
      const response = await makeAuthenticatedRequest(`/kb/articles/${articleId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      })
      if (!response.ok) throw new Error('Failed to update KB article')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to update KB article')
    }
  },

  async submitKBArticleFeedback(articleId: string, helpful: boolean) {
    try {
      const response = await makeAuthenticatedRequest(`/kb/articles/${articleId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ helpful })
      })
      if (!response.ok) throw new Error('Failed to submit feedback')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to submit feedback')
    }
  },

  // Public KB API (for client portal)
  async getPublicKBArticles(orgSlug: string, params: any = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await fetch(`${API_BASE_URL}/kb/public/${orgSlug}/articles?${queryString}`)
      if (!response.ok) throw new Error('Failed to fetch public articles')
      return await response.json()
    } catch (error) {
      handleApiError(error, 'Failed to fetch public articles')
    }
  },

  // Financial Management APIs
  
  // Invoices
  getInvoices: (params?: any) => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/invoices${queryString}`);
  },
  createInvoice: (data: any) => makeAuthenticatedRequest('/invoices', { method: 'POST', body: JSON.stringify(data) }),
  getInvoice: (id: string) => makeAuthenticatedRequest(`/invoices/${id}`),
  updateInvoice: (id: string, data: any) => makeAuthenticatedRequest(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInvoice: (id: string) => makeAuthenticatedRequest(`/invoices/${id}`, { method: 'DELETE' }),
  sendInvoice: (id: string, data?: any) => makeAuthenticatedRequest(`/invoices/${id}/send`, { method: 'POST', body: JSON.stringify(data) }),
  recordPayment: (id: string, data: any) => makeAuthenticatedRequest(`/invoices/${id}/payments`, { method: 'POST', body: JSON.stringify(data) }),
  generateInvoiceFromTime: (data: any) => makeAuthenticatedRequest('/invoices/generate-from-time', { method: 'POST', body: JSON.stringify(data) }),
  getInvoiceStats: (params?: any) => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/invoices/stats/summary${queryString}`);
  },

  // Expenses
  getExpenses: (params?: any) => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/expenses${queryString}`);
  },
  createExpense: (data: any) => makeAuthenticatedRequest('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  getExpense: (id: string) => makeAuthenticatedRequest(`/expenses/${id}`),
  updateExpense: (id: string, data: any) => makeAuthenticatedRequest(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExpense: (id: string) => makeAuthenticatedRequest(`/expenses/${id}`, { method: 'DELETE' }),
  submitExpense: (id: string) => makeAuthenticatedRequest(`/expenses/${id}/submit`),
  approveExpense: (id: string, data?: any) => makeAuthenticatedRequest(`/expenses/${id}/approve`, { method: 'POST', body: JSON.stringify(data) }),
  rejectExpense: (id: string, data?: any) => makeAuthenticatedRequest(`/expenses/${id}/reject`, { method: 'POST', body: JSON.stringify(data) }),
  bulkExpenseApproval: (data: any) => makeAuthenticatedRequest('/expenses/bulk-approval', { method: 'POST', body: JSON.stringify(data) }),
  getExpenseCategories: () => makeAuthenticatedRequest('/expenses/categories/list'),
  createExpenseCategory: (data: any) => makeAuthenticatedRequest('/expenses/categories', { method: 'POST', body: JSON.stringify(data) }),
  getExpenseStats: (params?: any) => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/expenses/stats/summary${queryString}`);
  },

  // Financial Reports
  getExecutiveDashboard: (period: string = 'month'): Promise<any> =>
    makeAuthenticatedRequest(`/financial-reports/executive-dashboard?period=${period}`),
  getProfitLossReport: (params: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/financial-reports/profit-loss${queryString}`);
  },
  getCashFlowReport: (params: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/financial-reports/cash-flow${queryString}`);
  },
  getARAgingReport: (params: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/financial-reports/ar-aging${queryString}`);
  },
  getProjectProfitabilityReport: (params: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/financial-reports/project-profitability${queryString}`);
  },
  getSavedReports: (): Promise<any> =>
    makeAuthenticatedRequest('/financial-reports/saved'),
  createSavedReport: (data: any): Promise<any> =>
    makeAuthenticatedRequest('/financial-reports/saved', { method: 'POST', body: JSON.stringify(data) }),

  // Contract Management
  // Contract Templates
  getContractTemplates: (): Promise<any> =>
    makeAuthenticatedRequest('/contracts/templates'),
  createContractTemplate: (data: any): Promise<any> =>
    makeAuthenticatedRequest('/contracts/templates', { method: 'POST', body: JSON.stringify(data) }),
  
  // Contracts
  getContracts: (params?: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/contracts${queryString}`);
  },
  getContract: (id: string): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}`),
  createContract: (data: any): Promise<any> =>
    makeAuthenticatedRequest('/contracts', { method: 'POST', body: JSON.stringify(data) }),
  updateContract: (id: string, data: any): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContract: (id: string): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}`, { method: 'DELETE' }),
  
  // Contract Actions
  approveContract: (id: string): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/approve`, { method: 'POST' }),
  activateContract: (id: string): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/activate`, { method: 'POST' }),
  renewContract: (id: string, data: any): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/renew`, { method: 'POST', body: JSON.stringify(data) }),
  
  // Contract Consumption
  getContractConsumption: (id: string, params?: any): Promise<any> => {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return makeAuthenticatedRequest(`/contracts/${id}/consumption${queryString}`);
  },
  recordContractConsumption: (id: string, data: any): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/consumption`, { method: 'POST', body: JSON.stringify(data) }),
  
  // Contract Rate Cards
  getContractRateCards: (id: string): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/rate-cards`),
  createContractRateCard: (id: string, data: any): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/${id}/rate-cards`, { method: 'POST', body: JSON.stringify(data) }),
  
  // Contract Statistics
  getContractStats: (period: string = 'month'): Promise<any> =>
    makeAuthenticatedRequest(`/contracts/stats/summary?period=${period}`),
}

export default { database, backendAPI }